import { parseSoundManifest } from "./manifest.js";
import {
  type ActiveVoice,
  admitPlay,
  createPickState,
  type PickState,
  pickVariation,
} from "./policy.js";
import { ownValue, type ResolvedSoundEvent, requireAsset, resolveSoundEvent } from "./resolve.js";
import type {
  SoundAsset,
  SoundBus,
  SoundCatalog,
  SoundEventMapping,
  SoundLayer,
  SoundManifest,
  SoundSource,
  SoundVariation,
} from "./types.js";

/**
 * How late a one-shot may start and still be worth playing: past its audio-clock target when
 * its file arrives late, or after the request while the first gesture is still resuming audio.
 */
const LATE_WINDOW_MS = 150;
/** A download that receives nothing for this long has stalled; the default fetcher aborts it. */
const IDLE_TIMEOUT_MS = 20_000;
/** How long a file that failed to load waits before it's fetched again. */
const RETRY_AFTER_MS = 30_000;
/** The loudest a theme, variation and caller together can push a sound. */
const MAX_BOOST_DB = 12;

const BUSES: readonly SoundBus[] = ["ui", "sfx", "music", "ambience"];

export type SoundEngineState = "locked" | "running" | "unsupported";

export interface PlayOptions {
  gainDb?: number;
  rate?: number;
  /** Start this many ms from now, on the audio clock. */
  atMs?: number;
  /** Start early so the asset's named cue lands `atMs` from now. */
  align?: { cue: string; atMs: number };
  /** Start this many ms into the sound, after any trim. */
  offsetMs?: number;
  fadeInMs?: number;
  /** Starting level, 0–1, on top of the mix's own gain. */
  level?: number;
}

export interface SoundHandle {
  stop(options?: { fadeMs?: number }): void;
  setRate(rate: number): void;
  /** Fades to `level` (0–1, on top of the mix's own gain) without restarting the sound. */
  setLevel(level: number, fadeMs?: number): void;
}

export interface ScheduledCue {
  id: string;
  atMs: number;
  gainDb?: number;
}

export interface SoundEngineOptions {
  catalog: SoundCatalog;
  createContext?: () => AudioContext | null;
  /**
   * Fetches a file's bytes. The default aborts a download that receives nothing for 20 s; one
   * passed in owns its own timeout.
   */
  fetchAudio?: (url: string) => Promise<ArrayBuffer>;
  nowMs?: () => number;
  random?: () => number;
  /** Try AAC before Opus; WebKit can't be trusted to report Opus-in-WebM support. */
  preferM4a?: boolean;
  /** Receives every swallowed failure (resume, fetch, decode) for breadcrumbs. */
  onError?: (error: unknown, context: string) => void;
}

export interface SoundEngine {
  readonly state: SoundEngineState;
  setLayers(layers: readonly SoundLayer[]): void;
  play(id: string, options?: PlayOptions): SoundHandle | null;
  /** Plays each cue at its offset; aborting stops the ones that haven't started. */
  schedule(cues: readonly ScheduledCue[], options?: { signal?: AbortSignal }): void;
  preload(ids: readonly string[]): void;
  /**
   * File sources of the event's first variation, preferred first, for media elements that
   * stream a long track themselves (e.g. a video-synced `<audio>`). Null when nothing maps it.
   */
  sourcesFor(id: string): SoundSource[] | null;
  /** What an event plays right now: the answering event (it or a fallback), its layer and mapping. */
  resolve(id: string): ResolvedSoundEvent | null;
  /** Voices of the event currently sounding, for the Sound Lab's meter. */
  activeVoiceCount(id: string): number;
  /** Call from a user gesture. Safe to call repeatedly; failures are swallowed. */
  unlock(): void;
  setHidden(hidden: boolean): void;
  setMuted(muted: boolean): void;
  setMasterVolume(volume: number): void;
  setBusVolume(bus: SoundBus, volume: number): void;
  setEventMuted(id: string, muted: boolean): void;
  /** Test and Sound Lab introspection. */
  debugMaster(): GainNode;
  debugBus(bus: SoundBus): GainNode;
}

const dbToGain = (db: number) => 10 ** (db / 20);

const defaultContext = (): AudioContext | null => {
  if (typeof window === "undefined") return null;
  const Ctor =
    window.AudioContext ??
    (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  return Ctor ? new Ctor({ latencyHint: "interactive" }) : null;
};

/**
 * Fetches `url`, aborting only when nothing arrives for `IDLE_TIMEOUT_MS`: before the response,
 * or between chunks of its body. A slow download that keeps arriving finishes; a stalled one is
 * cancelled, which frees its connection.
 */
const defaultFetch = async (url: string): Promise<ArrayBuffer> => {
  const controller = new AbortController();
  let timer = setTimeout(() => controller.abort(), IDLE_TIMEOUT_MS);
  const stillArriving = () => {
    clearTimeout(timer);
    timer = setTimeout(() => controller.abort(), IDLE_TIMEOUT_MS);
  };
  try {
    const response = await fetch(url, { signal: controller.signal });
    if (!response.ok) throw new Error(`Sound ${url} answered ${response.status}`);
    if (!response.body) return await response.arrayBuffer();
    stillArriving();
    const reader = response.body.getReader();
    const chunks: Uint8Array[] = [];
    let size = 0;
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      stillArriving();
      chunks.push(value);
      size += value.byteLength;
    }
    const bytes = new Uint8Array(size);
    let offset = 0;
    for (const chunk of chunks) {
      bytes.set(chunk, offset);
      offset += chunk.byteLength;
    }
    return bytes.buffer;
  } finally {
    clearTimeout(timer);
  }
};

/** A finite 0-1 volume or level, or null for anything else. */
const toUnitRange = (value: number) =>
  Number.isFinite(value) ? Math.min(1, Math.max(0, value)) : null;

/** True on iOS (every browser there is WebKit) and desktop Safari. */
export function prefersM4a(userAgent: string, maxTouchPoints = 0): boolean {
  const iOS =
    /iPad|iPhone|iPod/.test(userAgent) || (/Macintosh/.test(userAgent) && maxTouchPoints > 1);
  if (iOS) return true;
  return /Safari/.test(userAgent) && !/Chrome|Chromium|Edg|OPR|Firefox/.test(userAgent);
}

interface Voice extends ActiveVoice {
  source: AudioBufferSourceNode;
  gain: GainNode;
  baseLevel: number;
  startAt: number;
}

interface EventMemory {
  pick: PickState;
  lastStartMs: number | undefined;
  voices: Voice[];
}

/** One AudioContext for the app, never closed and never suspended on a timer. */
export function createSoundEngine(options: SoundEngineOptions): SoundEngine {
  const {
    catalog,
    createContext = defaultContext,
    fetchAudio = defaultFetch,
    nowMs = () => performance.now(),
    random = Math.random,
    onError = () => {},
  } = options;
  const preferM4a =
    options.preferM4a ??
    (typeof navigator !== "undefined" && prefersM4a(navigator.userAgent, navigator.maxTouchPoints));

  let layers: readonly SoundLayer[] = [];
  let ctx: AudioContext | null = null;
  let unsupported = false;
  let master: GainNode | null = null;
  const buses = new Map<SoundBus, GainNode>();
  let muted = false;
  let masterVolume = 1;
  const busVolumes = new Map<SoundBus, number>();
  const mutedEvents = new Set<string>();
  const buffers = new Map<string, Promise<AudioBuffer | null>>();
  /** When each asset last failed to load, so a broken file isn't refetched on every play. */
  const failedAt = new Map<string, number>();
  const decoded = new Map<string, AudioBuffer>();
  const memory = new Map<string, EventMemory>();
  /** Plays waiting for the context to run (loops only). */
  const waitingForRun = new Set<() => void>();
  let nextVoiceId = 1;
  /** Set while a gesture's resume() is in flight; one-shots fired meanwhile wait for it. */
  let isResumePending = false;
  /** Whether the context was running when the tab was last hidden. */
  let runningWhenHidden = false;

  /** Hands a failure to the host. A reporter that throws must not break playback too. */
  function report(error: unknown, context: string) {
    try {
      onError(error, context);
    } catch {
      // Nothing left to tell.
    }
  }
  const swallow = (context: string) => (error: unknown) => report(error, context);
  /** Runs `run`, reporting a throw instead of passing it on: a sound never breaks its caller. */
  function guard<T>(context: string, fallback: T, run: () => T): T {
    try {
      return run();
    } catch (error) {
      report(error, context);
      return fallback;
    }
  }

  /** Each layer's manifest as parsed, once per manifest object (layers are re-set often). */
  const parsedManifests = new WeakMap<object, SoundManifest>();
  function trusted(layer: SoundLayer): SoundLayer {
    const raw: unknown = layer.manifest;
    const cached = typeof raw === "object" && raw !== null ? parsedManifests.get(raw) : undefined;
    if (cached) return { name: layer.name, manifest: cached };
    const manifest = parseSoundManifest(raw, (issue) =>
      report(new Error(issue), `manifest ${layer.name}`),
    );
    if (typeof raw === "object" && raw !== null) parsedManifests.set(raw, manifest);
    return { name: layer.name, manifest };
  }

  function ensureContext(): AudioContext | null {
    if (ctx || unsupported) return ctx;
    // Build the whole graph before keeping the context, so a failure can't leave a half-wired
    // engine that later plays into nothing.
    try {
      const context = createContext();
      if (!context) {
        unsupported = true;
        return null;
      }
      const masterGain = context.createGain();
      masterGain.gain.value = muted ? 0 : masterVolume;
      const limiter = context.createDynamicsCompressor();
      limiter.threshold.value = -2;
      limiter.knee.value = 0;
      limiter.ratio.value = 20;
      limiter.attack.value = 0.002;
      limiter.release.value = 0.15;
      masterGain.connect(limiter);
      limiter.connect(context.destination);
      for (const bus of BUSES) {
        const gain = context.createGain();
        gain.gain.value = busVolumes.get(bus) ?? 1;
        gain.connect(masterGain);
        buses.set(bus, gain);
      }
      context.addEventListener("statechange", () => {
        if (ctx?.state !== "running") return;
        for (const start of [...waitingForRun]) start();
      });
      master = masterGain;
      ctx = context;
      return ctx;
    } catch (error) {
      report(error, "create");
      buses.clear();
      unsupported = true;
      return null;
    }
  }

  const isRunning = () => ctx?.state === "running";

  /** Moves a gain over ~150 ms (5 time constants), so mute and volume changes don't click. */
  function glide(node: GainNode, value: number) {
    const param = node.gain;
    const now = ctx?.currentTime ?? 0;
    // Pin where an unfinished glide got to; cancelling alone would snap back to its start.
    const current = param.value;
    param.cancelScheduledValues(now);
    param.setValueAtTime(current, now);
    param.setTargetAtTime(value, now, 0.03);
  }

  function memoryFor(id: string): EventMemory {
    let entry = memory.get(id);
    if (!entry) {
      entry = {
        pick: createPickState(),
        lastStartMs: undefined,
        voices: [],
      };
      memory.set(id, entry);
    }
    return entry;
  }

  const assetKey = (asset: SoundAsset) => asset.sources[0]?.url ?? "";

  function orderedSources(asset: SoundAsset) {
    if (!preferM4a) return asset.sources;
    const isMp4 = (type: string) => type.includes("mp4");
    return [...asset.sources].sort((a, b) => Number(isMp4(b.type)) - Number(isMp4(a.type)));
  }

  /** Loads an asset once. `retryNow` skips the wait after a failure (a loop asked to sound). */
  function loadAsset(asset: SoundAsset, retryNow = false): Promise<AudioBuffer | null> {
    const key = assetKey(asset);
    const cached = buffers.get(key);
    if (cached) return cached;
    const lastFailure = failedAt.get(key);
    if (!retryNow && lastFailure !== undefined && nowMs() - lastFailure < RETRY_AFTER_MS) {
      return Promise.resolve(null);
    }
    const pending = (async () => {
      for (const source of orderedSources(asset)) {
        const context = ensureContext();
        if (!context) return null;
        try {
          const bytes = await fetchAudio(source.url);
          const buffer = await context.decodeAudioData(bytes);
          decoded.set(key, buffer);
          return buffer;
        } catch (error) {
          report(error, `load ${source.url}`);
        }
      }
      return null;
    })();
    buffers.set(key, pending);
    // Forget a failed load so a play or preload after the wait tries again.
    void pending.then((buffer) => {
      if (buffer) {
        failedAt.delete(key);
        return;
      }
      failedAt.set(key, nowMs());
      if (buffers.get(key) === pending) buffers.delete(key);
    });
    return pending;
  }

  function startVoice(
    id: string,
    mapping: SoundEventMapping,
    bus: SoundBus,
    variation: SoundVariation,
    asset: SoundAsset,
    buffer: AudioBuffer,
    loop: boolean,
    play: PlayOptions,
    requestedAt: number,
  ): Voice | null {
    const context = ctx;
    if (!context) return null;
    const now = context.currentTime;
    let when = requestedAt + (play.atMs ?? 0) / 1000 + (mapping.delayMs ?? 0) / 1000;
    let skip = 0;
    if (play.align) {
      const cueMs = asset.cues?.[play.align.cue] ?? 0;
      when = requestedAt + (play.align.atMs - cueMs + (mapping.delayMs ?? 0)) / 1000;
      if (when < requestedAt) {
        skip = requestedAt - when;
        when = requestedAt;
      }
    }
    // A file that arrived after its target: a little late plays now, too late is dropped.
    if (when < now) {
      if (!loop && now - when > LATE_WINDOW_MS / 1000) return null;
      when = now;
    }

    const source = context.createBufferSource();
    source.buffer = buffer;
    source.playbackRate.value = play.rate ?? 1;
    if (mapping.pitchJitterCents) {
      source.detune.value = (random() * 2 - 1) * mapping.pitchJitterCents;
    }
    const jitterDb = mapping.gainJitterDb ? (random() * 2 - 1) * mapping.gainJitterDb : 0;
    const baseLevel = dbToGain(
      Math.min(
        MAX_BOOST_DB,
        (variation.gainDb ?? 0) + (mapping.gainDb ?? 0) + (play.gainDb ?? 0) + jitterDb,
      ),
    );
    const level = baseLevel * (toUnitRange(play.level ?? 1) ?? 1);
    const gain = context.createGain();
    try {
      const fadeInMs = play.fadeInMs ?? mapping.fadeInMs ?? 0;
      if (fadeInMs > 0) {
        gain.gain.setValueAtTime(0, when);
        gain.gain.linearRampToValueAtTime(level, when + fadeInMs / 1000);
      } else {
        gain.gain.value = level;
      }
      source.connect(gain);
      const busNode = buses.get(bus);
      if (busNode) gain.connect(busNode);

      const offset = ((variation.startMs ?? 0) + (play.offsetMs ?? 0)) / 1000 + skip;
      if (loop) {
        source.loop = true;
        if (typeof mapping.loop === "object") {
          source.loopStart = mapping.loop.startMs / 1000;
          source.loopEnd = mapping.loop.endMs / 1000;
        }
        source.start(when, offset);
      } else if (variation.endMs !== undefined) {
        const duration =
          (variation.endMs - (variation.startMs ?? 0) - (play.offsetMs ?? 0)) / 1000 - skip;
        source.start(when, offset, Math.max(0, duration));
      } else {
        source.start(when, offset);
      }
    } catch (error) {
      // A start that failed must not leave its nodes wired into the bus.
      source.disconnect();
      gain.disconnect();
      throw error;
    }

    const voice: Voice = {
      id: nextVoiceId++,
      startedMs: nowMs(),
      source,
      gain,
      baseLevel,
      startAt: when,
    };
    const entry = memoryFor(id);
    entry.voices.push(voice);
    source.onended = () => {
      entry.voices = entry.voices.filter((v) => v !== voice);
      source.disconnect();
      gain.disconnect();
    };
    return voice;
  }

  function stopVoice(id: string, voice: Voice, fadeMs: number) {
    const context = ctx;
    if (!context) return;
    const now = context.currentTime;
    try {
      if (fadeMs > 0) {
        voice.gain.gain.cancelScheduledValues(now);
        voice.gain.gain.setValueAtTime(voice.gain.gain.value, now);
        voice.gain.gain.linearRampToValueAtTime(0, now + fadeMs / 1000);
        voice.source.stop(now + fadeMs / 1000);
      } else {
        voice.source.stop(now);
      }
    } catch (error) {
      report(error, "stop");
      // The fade couldn't be scheduled; stop it now rather than leave it playing untracked.
      try {
        voice.source.stop(now);
      } catch (stopError) {
        report(stopError, "stop");
      }
    }
    const entry = memoryFor(id);
    entry.voices = entry.voices.filter((v) => v !== voice);
  }

  interface InternalHandle extends SoundHandle {
    cancelIfNotStarted(): void;
  }

  function play(id: string, options: PlayOptions = {}): InternalHandle | null {
    // The handle's setRate/setLevel write here, never into the caller's object.
    const playOptions: PlayOptions = { ...options };
    if (!ensureContext()) return null;
    const resolved = resolveSoundEvent(id, layers, catalog);
    if (!resolved || resolved.mapping.variations.length === 0) return null;
    const { mapping } = resolved;
    const spec = ownValue(catalog, id) ?? ownValue(catalog, resolved.id);
    const loop = Boolean(mapping.loop ?? spec?.loop);
    if (mutedEvents.has(id)) return null;
    if (!loop && muted) return null;
    // A one-shot fired by the gesture that's unlocking audio waits for the resume.
    const isAwaitingResume = !loop && !isRunning();
    if (isAwaitingResume && !isResumePending) return null;

    const entry = memoryFor(id);
    const now = nowMs();
    const admission = admitPlay(mapping, entry, now);
    if (!admission.admit) return null;

    const index = pickVariation(mapping.variations, mapping.select, entry.pick, random);
    const variation = mapping.variations[index];
    if (!variation) return null;
    const asset = requireAsset(resolved, variation);
    const bus = mapping.bus ?? spec?.bus ?? "sfx";
    const buffer = decoded.get(assetKey(asset));
    // A tick whose file isn't decoded yet is skipped, so it must not take a voice or a cooldown.
    if (!buffer && !loop && !isAwaitingResume && spec?.late === "drop") {
      void loadAsset(asset);
      return null;
    }

    if (admission.steal !== undefined) {
      const victim = entry.voices.find((v) => v.id === admission.steal);
      if (victim) stopVoice(id, victim, 0);
    }
    entry.lastStartMs = now;

    let voice: Voice | null = null;
    let stopped = false;
    const begin = (ready: AudioBuffer, requestedAt: number) => {
      if (stopped || voice) return;
      voice = guard(`start ${id}`, null, () =>
        startVoice(id, mapping, bus, variation, asset, ready, loop, playOptions, requestedAt),
      );
    };
    // The audio-clock time of the request, so a file that loads late still lands on target.
    const requestedAt = ctx?.currentTime ?? 0;
    let retryLoad: (() => void) | null = null;
    /** This play's entry in `waitingForRun`, dropped when it stops. */
    let waiter: (() => void) | null = null;

    const handle: InternalHandle = {
      stop: ({ fadeMs } = {}) => {
        stopped = true;
        if (waiter) waitingForRun.delete(waiter);
        if (voice) stopVoice(id, voice, fadeMs ?? mapping.fadeOutMs ?? 0);
      },
      setRate: (rate) =>
        guard(`setRate ${id}`, undefined, () => {
          if (voice) voice.source.playbackRate.value = rate;
          playOptions.rate = rate;
        }),
      setLevel: (level, fadeMs = 0) =>
        guard(`setLevel ${id}`, undefined, () => {
          const unit = toUnitRange(level);
          if (unit === null) throw new Error(`setLevel ${id}: ${level} is not a level`);
          level = unit;
          playOptions.level = level;
          // A loop whose file failed to load tries again when it's asked to sound.
          if (!voice && level > 0) retryLoad?.();
          const context = ctx;
          if (!voice || !context) return;
          const param = voice.gain.gain;
          const target = voice.baseLevel * level;
          const now = context.currentTime;
          param.cancelScheduledValues(now);
          if (fadeMs > 0) {
            param.setValueAtTime(param.value, now);
            param.linearRampToValueAtTime(target, now + fadeMs / 1000);
          } else {
            param.value = target;
          }
        }),
      cancelIfNotStarted: () => {
        if (!voice) {
          stopped = true;
          return;
        }
        if (ctx && voice.startAt > ctx.currentTime) stopVoice(id, voice, 0);
      },
    };

    if (isAwaitingResume) {
      const startOnRun = () => {
        waitingForRun.delete(startOnRun);
        // Wall clock: a resume that never came must not replay this on a later tap.
        if (nowMs() - now > LATE_WINDOW_MS) return;
        void loadAsset(asset).then((ready) => {
          if (ready && isRunning()) begin(ready, requestedAt);
        });
      };
      waitingForRun.add(startOnRun);
      waiter = startOnRun;
      void loadAsset(asset);
      return handle;
    }

    if (buffer && (isRunning() || !loop)) {
      begin(buffer, requestedAt);
      return handle;
    }

    if (loop) {
      const startWhenReady = () => {
        const ready = decoded.get(assetKey(asset));
        if (!ready || !isRunning()) return;
        waitingForRun.delete(startWhenReady);
        // A loop starts whenever it can, never late.
        begin(ready, ctx?.currentTime ?? 0);
      };
      waitingForRun.add(startWhenReady);
      waiter = startWhenReady;
      retryLoad = () => void loadAsset(asset, true).then(startWhenReady);
      retryLoad();
      return handle;
    }
    void loadAsset(asset).then((ready) => {
      if (!ready || !isRunning()) return;
      begin(ready, requestedAt);
    });
    return handle;
  }

  return {
    get state(): SoundEngineState {
      if (unsupported) return "unsupported";
      return isRunning() ? "running" : "locked";
    },
    setLayers(next) {
      guard("setLayers", undefined, () => {
        layers = next.map(trusted);
      });
    },
    play: (id, options) => guard(`play ${id}`, null, () => play(id, options)),
    schedule(cues, { signal } = {}) {
      guard("schedule", undefined, () => {
        if (signal?.aborted) return;
        const handles = cues
          .map((cue) =>
            guard(`play ${cue.id}`, null, () =>
              play(cue.id, { atMs: cue.atMs, gainDb: cue.gainDb }),
            ),
          )
          .filter((handle): handle is InternalHandle => handle !== null);
        if (!signal || handles.length === 0) return;
        const cancel = () => {
          for (const handle of handles) guard("cancel", undefined, handle.cancelIfNotStarted);
        };
        signal.addEventListener("abort", cancel, { once: true });
        // Every cue has started or been dropped a moment after the last one is due, so let go
        // of the signal then: a caller may reuse one for a whole session.
        const lastAtMs = Math.max(0, ...cues.map((cue) => cue.atMs).filter(Number.isFinite));
        setTimeout(
          () => signal.removeEventListener("abort", cancel),
          lastAtMs + LATE_WINDOW_MS + 5_000,
        );
      });
    },
    resolve: (id) => guard(`resolve ${id}`, null, () => resolveSoundEvent(id, layers, catalog)),
    activeVoiceCount(id) {
      return memory.get(id)?.voices.length ?? 0;
    },
    sourcesFor: (id) =>
      guard(`sourcesFor ${id}`, null, () => {
        const resolved = resolveSoundEvent(id, layers, catalog);
        const first = resolved?.mapping.variations[0];
        if (!resolved || !first) return null;
        return [...orderedSources(requireAsset(resolved, first))];
      }),
    preload(ids) {
      for (const id of ids) {
        guard(`preload ${id}`, undefined, () => {
          const resolved = resolveSoundEvent(id, layers, catalog);
          if (!resolved) return;
          for (const variation of resolved.mapping.variations) {
            void loadAsset(requireAsset(resolved, variation));
          }
        });
      }
    },
    unlock() {
      guard("unlock", undefined, () => {
        const context = ensureContext();
        if (!context || context.state === "running") return;
        isResumePending = true;
        context
          .resume()
          .catch(swallow("resume"))
          .finally(() => {
            isResumePending = false;
          });
      });
    },
    setHidden(hidden) {
      guard("setHidden", undefined, () => {
        if (!ctx) return;
        // Keep running while hidden so background chimes (gem drops) still sound; only
        // bring back a context the browser suspended meanwhile (iOS does on background).
        if (hidden) {
          runningWhenHidden = ctx.state === "running";
        } else if (runningWhenHidden && ctx.state !== "running") {
          runningWhenHidden = false;
          ctx.resume().catch(swallow("resume"));
        }
      });
    },
    setMuted(next) {
      guard("setMuted", undefined, () => {
        muted = next;
        if (master) glide(master, muted ? 0 : masterVolume);
      });
    },
    setMasterVolume(volume) {
      guard("setMasterVolume", undefined, () => {
        const unit = toUnitRange(volume);
        if (unit === null) throw new Error(`setMasterVolume: ${volume} is not a volume`);
        masterVolume = unit;
        if (master && !muted) glide(master, unit);
      });
    },
    setBusVolume(bus, volume) {
      guard(`setBusVolume ${bus}`, undefined, () => {
        const unit = toUnitRange(volume);
        if (unit === null) throw new Error(`setBusVolume ${bus}: ${volume} is not a volume`);
        busVolumes.set(bus, unit);
        const node = buses.get(bus);
        if (node) glide(node, unit);
      });
    },
    setEventMuted(id, isMuted) {
      if (isMuted) mutedEvents.add(id);
      else mutedEvents.delete(id);
    },
    debugMaster() {
      ensureContext();
      return master as GainNode;
    },
    debugBus(bus) {
      ensureContext();
      return buses.get(bus) as GainNode;
    },
  };
}
